import React, { useEffect, useState, useCallback, useRef } from "react";
import { getClassifiedTransactions, createTransactionOverride, deleteTransactionOverride } from "../../api/savingsClient";
import { formatCurrency, formatDate } from "../../utils/formatters";

const CATEGORIES = [
    "FOOD_AND_DRINK",
    "TRANSPORTATION",
    "GENERAL_MERCHANDISE",
    "ENTERTAINMENT",
    "RENT_AND_UTILITIES",
    "GENERAL_SERVICES",
    "TRAVEL",
    "MEDICAL",
    "PERSONAL_CARE",
    "GOVERNMENT_AND_NON_PROFIT",
    "EDUCATION",
    "BANK_FEES_AND_CHARGES",
    "HOME_IMPROVEMENT",
    "INCOME",
    "OTHER",
];

const FILTER_TABS = [
    { key: "ALL", label: "All" },
    { key: "INCOME", label: "Income" },
    { key: "EXPENSE", label: "Expenses" },
    { key: "TRANSFER", label: "Transfers" },
    { key: "EXCLUDED", label: "Excluded" },
];

function formatCategoryLabel(cat) {
    if (!cat) return "Unknown";
    return cat
        .split("_")
        .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
        .join(" ");
}

function getAmountColor(classification, overrideType) {
    if (overrideType === "EXCLUDE") return "text-gray-400";
    switch (classification) {
        case "INCOME":
            return "text-accent-600";
        case "EXPENSE":
            return "text-danger-500";
        case "TRANSFER":
            return "text-gray-500";
        default:
            return "text-gray-700";
    }
}

function classificationBadge(classification, overrideType) {
    if (overrideType === "EXCLUDE") {
        return "bg-gray-100 text-gray-500";
    }
    switch (classification) {
        case "INCOME":
            return "bg-accent-50 text-accent-600";
        case "EXPENSE":
            return "bg-red-50 text-danger-600";
        case "TRANSFER":
            return "bg-gray-100 text-gray-600";
        default:
            return "bg-gray-100 text-gray-600";
    }
}

export default function TransactionReviewModal({ isOpen, onClose, onChanged }) {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("ALL");
    const [hasChanges, setHasChanges] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const abortRef = useRef(null);

    const fetchTransactions = useCallback(async () => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        setLoading(true);
        try {
            const resp = await getClassifiedTransactions(6, abortRef.current.signal);
            setTransactions(resp.transactions || []);
        } catch (e) {
            if (e.name === "AbortError" || e.name === "CanceledError") return;
            console.error("Failed to load transactions", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            setHasChanges(false);
            setFilter("ALL");
            fetchTransactions();
        }
        return () => abortRef.current?.abort();
    }, [isOpen, fetchTransactions]);

    const handleClose = useCallback(() => {
        const shouldRefresh = hasChanges;
        onClose();
        if (shouldRefresh && onChanged) {
            // Small delay to ensure backend transaction commits before refetch
            setTimeout(() => onChanged(), 100);
        }
    }, [hasChanges, onChanged, onClose]);

    const handleExclude = useCallback(async (txn) => {
        setActionLoading(txn.transactionId);
        try {
            await createTransactionOverride(txn.transactionId, "EXCLUDE");
            setTransactions((prev) =>
                prev.map((t) =>
                    t.transactionId === txn.transactionId
                        ? { ...t, overrideType: "EXCLUDE", effectiveCategory: t.effectiveCategory }
                        : t
                )
            );
            setHasChanges(true);
        } catch (e) {
            console.error("Failed to exclude transaction", e);
        } finally {
            setActionLoading(null);
        }
    }, []);

    const handleInclude = useCallback(async (txn) => {
        setActionLoading(txn.transactionId);
        try {
            await deleteTransactionOverride(txn.transactionId);
            setTransactions((prev) =>
                prev.map((t) =>
                    t.transactionId === txn.transactionId
                        ? { ...t, overrideType: null, effectiveCategory: t.originalCategory }
                        : t
                )
            );
            setHasChanges(true);
        } catch (e) {
            console.error("Failed to include transaction", e);
        } finally {
            setActionLoading(null);
        }
    }, []);

    const handleReclassify = useCallback(async (txn, newCategory) => {
        if (!newCategory || newCategory === txn.effectiveCategory) return;
        setActionLoading(txn.transactionId);
        try {
            await createTransactionOverride(txn.transactionId, "RECLASSIFY", newCategory);
            setTransactions((prev) =>
                prev.map((t) =>
                    t.transactionId === txn.transactionId
                        ? { ...t, overrideType: "RECLASSIFY", effectiveCategory: newCategory }
                        : t
                )
            );
            setHasChanges(true);
        } catch (e) {
            console.error("Failed to reclassify transaction", e);
        } finally {
            setActionLoading(null);
        }
    }, []);

    const handleResetCategory = useCallback(async (txn) => {
        setActionLoading(txn.transactionId);
        try {
            await deleteTransactionOverride(txn.transactionId);
            setTransactions((prev) =>
                prev.map((t) =>
                    t.transactionId === txn.transactionId
                        ? { ...t, overrideType: null, effectiveCategory: t.originalCategory }
                        : t
                )
            );
            setHasChanges(true);
        } catch (e) {
            console.error("Failed to reset category", e);
        } finally {
            setActionLoading(null);
        }
    }, []);

    const filteredTransactions = transactions.filter((txn) => {
        if (filter === "ALL") return true;
        if (filter === "EXCLUDED") return txn.overrideType === "EXCLUDE";
        if (filter === "INCOME") return txn.classification === "INCOME" && txn.overrideType !== "EXCLUDE";
        if (filter === "EXPENSE") return txn.classification === "EXPENSE" && txn.overrideType !== "EXCLUDE";
        if (filter === "TRANSFER") return txn.classification === "TRANSFER" && txn.overrideType !== "EXCLUDE";
        return true;
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="absolute inset-0 bg-gray-900/40"
                onClick={handleClose}
            />
            <div className="relative w-full max-w-4xl max-h-[90vh] bg-white border border-gray-200 rounded-xl shadow-lg flex flex-col mx-4">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Review transactions</h2>
                        <p className="text-sm text-gray-500 mt-0.5">
                            Exclude or reclassify transactions to refine your savings rate
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-900 transition-colors text-xl leading-none p-1"
                        aria-label="Close"
                    >
                        &times;
                    </button>
                </div>

                {/* Filter Tabs */}
                <div className="flex gap-1 px-6 py-3 border-b border-gray-200">
                    {FILTER_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setFilter(tab.key)}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                filter === tab.key
                                    ? "bg-gray-900 text-white"
                                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Transaction List */}
                <div className="flex-1 overflow-y-auto px-6 py-3">
                    {loading ? (
                        <div className="space-y-3">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />
                            ))}
                        </div>
                    ) : filteredTransactions.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500 text-sm">No transactions found</p>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {filteredTransactions.map((txn) => {
                                const isExcluded = txn.overrideType === "EXCLUDE";
                                const isReclassified = txn.overrideType === "RECLASSIFY";
                                const isActionPending = actionLoading === txn.transactionId;

                                return (
                                    <div
                                        key={txn.transactionId}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                                            isExcluded
                                                ? "bg-gray-50 opacity-60"
                                                : "hover:bg-gray-50"
                                        }`}
                                    >
                                        {/* Date */}
                                        <span
                                            className={`text-sm w-24 shrink-0 ${
                                                isExcluded ? "line-through text-gray-400" : "text-gray-500"
                                            }`}
                                        >
                                            {formatDate(txn.date)}
                                        </span>

                                        {/* Name / Merchant */}
                                        <div className="flex-1 min-w-0">
                                            <span
                                                className={`text-sm block truncate ${
                                                    isExcluded ? "line-through text-gray-400" : "text-gray-900"
                                                }`}
                                            >
                                                {txn.name || txn.merchantName || formatCategoryLabel(txn.effectiveCategory) || "Unknown transaction"}
                                            </span>
                                            {txn.merchantName && txn.name && txn.merchantName !== txn.name && (
                                                <span className="text-xs text-gray-500 block truncate">
                                                    {txn.merchantName}
                                                </span>
                                            )}
                                            {!txn.name && !txn.merchantName && txn.effectiveCategory && (
                                                <span className="text-xs text-gray-400 block truncate italic">
                                                    No merchant info
                                                </span>
                                            )}
                                        </div>

                                        {/* Amount */}
                                        <span
                                            className={`text-sm font-medium w-24 text-right shrink-0 ${
                                                isExcluded ? "line-through" : ""
                                            } ${getAmountColor(txn.classification, txn.overrideType)}`}
                                        >
                                            {formatCurrency(Math.abs(txn.amount))}
                                        </span>

                                        {/* Category Badge */}
                                        <span
                                            className={`text-xs px-2 py-0.5 rounded-full w-28 text-center shrink-0 ${classificationBadge(
                                                txn.classification,
                                                txn.overrideType
                                            )}`}
                                        >
                                            {isExcluded
                                                ? "Excluded"
                                                : formatCategoryLabel(txn.effectiveCategory)}
                                        </span>

                                        {/* Actions */}
                                        <div className="flex items-center gap-2 shrink-0 w-48 justify-end">
                                            {/* Category dropdown for expenses */}
                                            {txn.classification === "EXPENSE" && !isExcluded && (
                                                <div className="flex items-center gap-1">
                                                    <select
                                                        value={txn.effectiveCategory || ""}
                                                        onChange={(e) =>
                                                            handleReclassify(txn, e.target.value)
                                                        }
                                                        disabled={isActionPending}
                                                        className="bg-white border border-gray-200 text-gray-700 text-xs rounded px-1.5 py-1 w-28 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500/20"
                                                    >
                                                        {CATEGORIES.map((cat) => (
                                                            <option key={cat} value={cat}>
                                                                {formatCategoryLabel(cat)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {isReclassified && (
                                                        <button
                                                            onClick={() => handleResetCategory(txn)}
                                                            disabled={isActionPending}
                                                            className="text-xs text-gray-500 hover:text-gray-900 transition-colors"
                                                            title="Reset to original category"
                                                        >
                                                            Reset
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            {/* Exclude / Include toggle */}
                                            {isExcluded ? (
                                                <button
                                                    onClick={() => handleInclude(txn)}
                                                    disabled={isActionPending}
                                                    className="text-xs px-2 py-1 rounded text-accent-600 hover:bg-accent-50 transition-colors disabled:opacity-50"
                                                >
                                                    Include
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleExclude(txn)}
                                                    disabled={isActionPending}
                                                    className="text-xs px-2 py-1 rounded text-gray-500 hover:text-danger-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                                                >
                                                    Exclude
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                        {transactions.length} transactions
                        {transactions.filter((t) => t.overrideType === "EXCLUDE").length > 0 &&
                            ` / ${transactions.filter((t) => t.overrideType === "EXCLUDE").length} excluded`}
                    </p>
                    <button
                        onClick={handleClose}
                        className="btn-primary px-4 py-2 text-sm"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}
